"use client";
import { CustomButton, FlexCenter, FlexCol } from "@/components";
import { cn } from "@/lib";
import { useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import { routes } from "@/routes";
import React, { useEffect, useState } from "react";
import { useModalStore } from "@/store";
import { usePurchaseFileMutation } from "@/hooks";
import { useToastStore } from "@/store/toastStore";
import { useSession } from "next-auth/react";
import VehiclesPaymentModal from "@/components/Payment/vehiclesPayment/vehiclePaymentModal";
import ProtectedImage from "@/components/ui/protectedImage";
import { IRawPurchasedTrafficFinesPhotoResData } from "@/interfaces/inquiries/vehicle";

const FinesPhotoInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedTrafficFinesPhotoResData;
}) => {
  const { data: session } = useSession();
  const finesImages = data.data.data.body;
  const servicePdfFee = (data as { inquery?: { pdf_fee?: string | number | null } })
    .inquery?.pdf_fee;
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const searchParams = useSearchParams();
  const { openModal, closeModal, setOpenModal } = useModalStore();
  const {
    isPending: isFileSubmitting,
    mutate: purchaseFileFn,
    error: purchaseFileError,
    reset: resetPurchaseFile,
  } = usePurchaseFileMutation(session?.user.token);

  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerEndTime, setTimerEndTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { setOpenToast } = useToastStore();
  const openImg = (image: string) => {
    window.open(image, "_blank");
  };
  const handleTimerComplete = () => {
    setIsTimerActive(false);
    setTimerEndTime(null);
  };
  const handleCloseModal = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("download");
    url.searchParams.delete("payment");
    window.history.replaceState({}, "", url.toString());
    closeModal();
  };
  // const downloadFiles = async () => {
  //   const urls = [
  //     { url: finesImages.vehicleImageUrl, name: "vehicle" },
  //     { url: finesImages.plateImageUrl, name: "plate" },
  //   ];
  //
  //   for (const { url, name } of urls) {
  //     try {
  //       // Extract file extension from URL
  //       const extension = url.split(".").pop()?.split("?")[0] || "jpg";
  //
  //       // Use Next.js API route as proxy to avoid CORS
  //       const response = await fetch(
  //         `/api/download-phone-bill-image?url=${encodeURIComponent(url)}`,
  //       );
  //
  //       if (!response.ok) {
  //         console.error(`Failed to download ${name} phone-bill-image`);
  //         continue;
  //       }
  //
  //       const blob = await response.blob();
  //       const objectURL = URL.createObjectURL(blob);
  //
  //       const link = document.createElement("a");
  //       link.href = objectURL;
  //       link.download = `${data.data.data.body.serialNo}-${name}-${Date.now()}.${extension}`;
  //       document.body.appendChild(link);
  //       link.click();
  //       link.remove();
  //       URL.revokeObjectURL(objectURL);
  //     } catch (error) {
  //       console.error(`Error downloading ${name} phone-bill-image:`, error);
  //     }
  //   }
  // };

  const downloadFiles = async () => {
    const urls = [
      { url: finesImages.vehicleImageUrl, name: "vehicle" },
      { url: finesImages.plateImageUrl, name: "plate" },
    ];

    for (const { url, name } of urls) {
      try {
        // Extract file extension from URL
        const extension = url.split(".").pop()?.split("?")[0] || "jpg";

        // Use Next.js API route as proxy to avoid CORS
        const response = await fetch(
          `/api/download-image?url=${encodeURIComponent(url)}`,
        );

        if (!response.ok) {
          console.error(`Failed to download ${name} image`);
          continue;
        }

        const blob = await response.blob();
        const objectURL = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = objectURL;
        link.download = `${data.data.data.body.serialNo}-${name}-${Date.now()}.${extension}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectURL);

        // Add delay between downloads to prevent multiple prompts
        // Wait 300ms before starting next download
        if (urls.indexOf({ url, name }) < urls.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      } catch (error) {
        console.error(`Error downloading ${name} image:`, error);
      }
    }
  };
  const downloadPaymentSubmission = () => {
    purchaseFileFn(
      { payload: { purchase_id: data.id } },
      {
        onSuccess: (data) => {
          if (data.success) {
            setOpenToast({
              variant: "success",
              message: "خرید با موفقیت انجام شد",
              position: "top-center",
            });
            setTimeout(async () => {
              // setOpenToast({
              //   variant: "success",
              //   message: "شروع دانلود فایل",
              //   position: "top-center",
              // });
              await downloadFiles();
            }, 1000);
            handleCloseModal();
          }
          setError(null);
          setIsTimerActive(false);
          setTimerEndTime(null);
        },
        onError: (error) => {
          if (error) {
            setError(error.message);
            setIsTimerActive(true); // Start timer
            setTimerEndTime(Date.now() + 60000);
          }
        },
      },
    );
  };
  useEffect(() => {
    const shouldDownload = searchParams?.get("download");
    if (shouldDownload) {
      setOpenModal("vehicle-payment-method");
      // if (shouldDownload === "true") {
      //   // Small delay to ensure component is fully mounted
      //   setTimeout(() => {
      //     downloadPaymentSubmission();
      //   }, 500);
      // } else {
      //   setOpenModal("vehicle-payment-method");
      // }
    }
  }, [searchParams]);
  return (
    <div className="shadow-main from-sky-bliss relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          تصویر خلافی خودرو
        </h2>

        {/*<div className="mb-3 items-center">*/}
        {/*  <h2 className="text-lg font-bold">*/}
        {/*    {t("accountToIban.resultTitle")}*/}
        {/*  </h2>*/}
        {/*  <div className="mt-3 rounded-lg border border-blue-300 bg-blue-100 p-2">*/}
        {/*    <p className="text-sm">*/}
        {/*      برای بزرگ نمایی تصاویر، روی تصویر کلیک کنید.*/}
        {/*    </p>*/}
        {/*  </div>*/}
        {/*</div>*/}
        <div className="mt-3 flex items-center gap-2">
          <ProtectedImage
            src={finesImages.plateImageUrl}
            width="300"
            height="300"
            alt="تصویر پلاک"
            className="cursor-pointer rounded-md object-contain"
          />
          <ProtectedImage
            src={finesImages.vehicleImageUrl}
            width="300"
            height="300"
            alt="تصویر تخلف"
            className="cursor-pointer rounded-md object-contain"
          />
        </div>

        <FlexCenter className="mt-10 flex flex-col justify-center md:flex-row md:gap-6">
          <CustomButton
            id="details-fines-photo-inquiry-back"
            className="order-2 flex w-fit cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            onClick={() => setOpenModal("vehicle-payment-method")}
          >
            دانلود تصاویر
          </CustomButton>
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-fines-photo-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-fines-photo-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Fines_Photo.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {openModal === "vehicle-payment-method" && (
        <VehiclesPaymentModal
          payBtnId="payment-file-submit"
          submissionError={purchaseFileError}
          paymentSubmission={downloadPaymentSubmission}
          serviceCost={servicePdfFee}
          onClose={handleCloseModal}
          isOpen={openModal === "vehicle-payment-method"}
          submissionLoading={isFileSubmitting}
          onErrorReset={resetPurchaseFile}
          showManualError={error}
          isTimerActive={isTimerActive}
          timerEndTime={timerEndTime}
          onTimerComplete={handleTimerComplete}
        />
      )}
    </div>
  );
};

export default FinesPhotoInquiryDetails;
